import React, { useState } from 'react';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { CallbackModal } from '../../components/CallbackModal';
import { Calendar, Clock, Tag, ChevronLeft, MessageCircle, Plus, Minus, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../i18n';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-1006">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-1007">
        <span className="text-lg font-medium text-gray-900 group-hover:text-teal-600 transition-colors" data-id="element-1008">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-1009">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-1010" /> : <Plus className="w-4 h-4" data-id="element-1011" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-1012">
        <p className="text-gray-600 leading-relaxed" data-id="element-1013">{answer}</p>
      </div>
    </div>;
}
export function WithoutInsuranceArticle() {
  const {
    lang
  } = useTranslation();
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const pick = (en: string, ua: string, ru: string, ro: string) => {
    if (lang === 'ua') return ua;
    if (lang === 'ru') return ru;
    if (lang === 'ro') return ro;
    return en;
  };
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    q: pick('Do I need insurance to visit a dentist?', 'Чи потрібна страховка для візиту до стоматолога?', 'Нужна ли страховка для визита к стоматологу?', 'Am nevoie de asigurare pentru a vizita un dentist?'),
    a: pick('No, in the UK dental insurance is not mandatory. You can simply book an appointment and pay as you go.', "Ні, у Великобританії стоматологічна страховка не є обов'язковою. Ви можете просто записатися на прийом і оплатити його (pay-as-you-go).", 'Нет, в Великобритании стоматологическая страховка не является обязательной. Вы можете просто записаться на прием и оплатить его (pay-as-you-go).', 'Nu, în Marea Britanie asigurarea dentară nu este obligatorie. Puteți pur și simplu să faceți o programare și să plătiți pe măsură ce mergeți.')
  }, {
    q: pick('How much does a visit cost without insurance?', 'Скільки коштує візит без страховки?', 'Сколько стоит визит без страховки?', 'Cât costă o vizită fără asigurare?'),
    a: pick('In private clinics, an examination costs from £40 to £100. Treatment is paid separately according to the price list.', 'У приватних клініках огляд коштує від £40 до £100. Лікування оплачується окремо згідно з прайсом.', 'В частных клиниках осмотр стоит от £40 до £100. Лечение оплачивается отдельно согласно прайсу.', 'În clinicile private, o examinare costă de la 40£ la 100£. Tratamentul este plătit separat conform listei de prețuri.')
  }, {
    q: pick('Does Dentico Clinic accept insurance?', 'Чи приймає Dentico Clinic страховки?', 'Принимает ли Dentico Clinic страховки?', 'Dentico Clinic acceptă asigurări?'),
    a: pick('We work as a pay-as-you-go clinic, but we provide all the necessary documents (invoices) so that you can get a reimbursement from your insurance company if you have one.', 'Ми працюємо як pay-as-you-go клініка, але надаємо всі необхідні документи (інвойси), щоб ви могли отримати відшкодування від вашої страхової компанії, якщо вона у вас є.', 'Мы работаем как pay-as-you-go клиника, но предоставляем все необходимые документы (инвойсы), чтобы вы могли получить возмещение от вашей страховой компании, если она у вас есть.', 'Lucrăm ca o clinică pay-as-you-go, dar oferim toate documentele necesare (facturi) astfel încât să puteți obține o rambursare de la compania dumneavoastră de asigurări, dacă aveți una.')
  }];
  return <Layout data-id="element-1014">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-1015" />

      <article className="bg-white" data-id="element-1016">
        {/* Header */}
        <div className="bg-teal-50 py-12 lg:py-20" data-id="element-1017">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1018">
            <Link to={`/${lang}/blog`} className="inline-flex items-center text-teal-600 font-medium mb-6 hover:underline" data-id="element-1019">
              <ChevronLeft className="w-4 h-4 mr-1" data-id="element-1020" />
              {pick('Back to blog', 'Назад до блогу', 'Назад в блог', 'Înapoi la blog')}
            </Link>

            <div className="flex flex-wrap gap-4 mb-6 text-sm text-gray-600" data-id="element-1021">
              <span className="bg-white px-3 py-1 rounded-full font-medium text-teal-700 flex items-center shadow-sm" data-id="element-1022">
                <Tag className="w-3 h-3 mr-2" data-id="element-1023" />
                {pick('Tips', 'Поради', 'Советы', 'Sfaturi')}
              </span>
              <span className="flex items-center" data-id="element-1024">
                <Calendar className="w-4 h-4 mr-2" data-id="element-1025" />
                {pick('February 2026', 'Лютий 2026', 'Февраль 2026', 'Februarie 2026')}
              </span>
              <span className="flex items-center" data-id="element-1026">
                <Clock className="w-4 h-4 mr-2" data-id="element-1027" />
                {pick('7 min read', '7 хв читання', '7 мин чтения', '7 min citire')}
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6" data-id="element-1028">
              {pick('Can you get dental treatment in the UK without insurance?', 'Чи можна лікувати зуби в UK без страховки? Гід для українців', 'Можно ли лечить зубы в UK без страховки? Гид для пациентов', 'Vă puteți trata dinții în UK fără asigurare? Ghid pentru pacienți')}
            </h1>

            <div className="flex items-center" data-id="element-1029">
              <div className="w-10 h-10 bg-teal-200 rounded-full flex items-center justify-center text-teal-700 font-bold mr-3" data-id="element-1030">
                DC
              </div>
              <div data-id="element-1031">
                <p className="font-medium text-gray-900" data-id="element-1032">Dentico Clinic Team</p>
                <p className="text-sm text-gray-500" data-id="element-1033">
                  {pick('Expert Article', 'Експертна стаття', 'Экспертная статья', 'Articol Expert')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-1034">
          <div className="prose prose-lg prose-teal max-w-none text-gray-600" data-id="element-1035">
            <p className="lead text-xl text-gray-800 font-medium mb-8" data-id="element-1036">
              {pick('Many people coming to the UK are used to medicine being insurance-based. But does this apply to dentistry? Can you go to a doctor if you do not have a policy? The short answer is: YES. Here is how it works.', 'Багато українців, які приїжджають до Великобританії, звикли до того, що медицина тут страхова. Але чи стосується це стоматології? Чи можна піти до лікаря, якщо у вас немає полісу? Коротка відповідь: ТАК. Розповідаємо, як це працює.', 'Многие люди, приезжающие в Великобританию, привыкли к тому, что медицина здесь страховая. Но относится ли это к стоматологии? Можно ли пойти к врачу, если у вас нет полиса? Короткий ответ: ДА. Рассказываем, как это работает.', 'Mulți oameni care vin în Marea Britanie sunt obișnuiți ca medicina să fie bazată pe asigurări. Dar se aplică acest lucru și stomatologiei? Puteți merge la un medic dacă nu aveți o poliță? Răspunsul scurt este: DA. Iată cum funcționează.')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-1037">
              {pick('Dentistry in the UK: Insurance is not mandatory', "Стоматологія в UK: Страховка не обов'язкова", 'Стоматология в UK: Страховка не обязательна', 'Stomatologia în UK: Asigurarea nu este obligatorie')}
            </h2>
            <p className="mb-6" data-id="element-1038">
              {pick('Unlike the US or some European countries, access to dentistry in the UK does not depend on having insurance. Most British people treat their teeth on a "Pay-as-you-go" basis.', 'На відміну від США чи деяких країн Європи, у Великобританії доступ до стоматології не залежить від наявності страховки. Більшість британців лікують зуби за принципом "Pay-as-you-go" (оплата за фактом візиту).', 'В отличие от США или некоторых стран Европы, доступ к стоматологии в Великобритании не зависит от наличия страховки. Большинство британцев лечат зубы по принципу "Pay-as-you-go" (оплата по факту визита).', 'Spre deosebire de SUA sau unele țări europene, accesul la stomatologie în Marea Britanie nu depinde de a avea o asigurare. Majoritatea britanicilor își tratează dinții pe principiul "Pay-as-you-go" (plată la vizită).')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-1039">
              {pick('Private dentistry without insurance', 'Приватна стоматологія без страховки', 'Частная стоматология без страховки', 'Stomatologie privată fără asigurare')}
            </h2>
            <p className="mb-6" data-id="element-1040">
              {pick('Private clinics, such as Dentico Clinic, accept all patients. You do not need NHS registration or insurance.', 'Приватні клініки, такі як Dentico Clinic, приймають всіх пацієнтів. Вам не потрібна ні реєстрація в NHS, ні страховка.', 'Частные клиники, такие как Dentico Clinic, принимают всех пациентов. Вам не нужна ни регистрация в NHS, ни страховка.', 'Clinicile private, cum ar fi Dentico Clinic, acceptă toți pacienții. Nu aveți nevoie de înregistrare NHS sau asigurare.')}
            </p>
            <p className="mb-6" data-id="element-1041">
              <strong data-id="element-1042">
                {pick('Benefits:', 'Переваги:', 'Преимущества:', 'Beneficii:')}
              </strong>
            </p>
            <ul className="space-y-3 mb-8" data-id="element-1043">
              <li className="flex items-start" data-id="element-1044">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-1045" />
                <span data-id="element-1046">
                  {pick('You only pay for what you get done.', 'Ви платите лише за те, що вам зробили.', 'Вы платите только за то, что вам сделали.', 'Plătiți doar pentru ceea ce vi se face.')}
                </span>
              </li>
              <li className="flex items-start" data-id="element-1047">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-1048" />
                <span data-id="element-1049">
                  {pick('No monthly payments if you do not use the services.', 'Немає щомісячних платежів, якщо ви не користуєтесь послугами.', 'Нет ежемесячных платежей, если вы не пользуетесь услугами.', 'Fără plăți lunare dacă nu utilizați serviciile.')}
                </span>
              </li>
              <li className="flex items-start" data-id="element-1050">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-1051" />
                <span data-id="element-1052">
                  {pick('Transparent prices (you see the price list).', 'Прозорі ціни (ви бачите прайс-лист).', 'Прозрачные цены (вы видите прайс-лист).', 'Prețuri transparente (vedeți lista de prețuri).')}
                </span>
              </li>
            </ul>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 pt-10 border-t border-gray-100" data-id="element-1053">
            <h3 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-1054">
              {pick('Frequently Asked Questions', 'Часті запитання', 'Частые вопросы', 'Întrebări Frecvente')}
            </h3>
            <div className="space-y-2" data-id="element-1055">
              {faqs.map((faq, idx) => <AccordionItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => toggleFaq(idx)} data-id="element-1056" />)}
            </div>
          </div>

          {/* Read Also */}
          <div className="mt-16" data-id="element-1057">
            <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1058">
              {pick('Read Also', 'Читайте також', 'Читайте также', 'Citește și')}
            </h3>
            <div className="grid sm:grid-cols-2 gap-4" data-id="element-1059">
              <Link to={`/${lang}/blog/nhs-vs-private-dentistry-uk`} data-id="element-1060">
                <Card className="p-4 hover:shadow-md transition-shadow h-full flex items-center" data-id="element-1061">
                  <span className="text-teal-600 font-medium" data-id="element-1062">
                    {pick('NHS vs Private Dentistry', 'NHS vs Приватна стоматологія', 'NHS vs Частная стоматология', 'NHS vs Stomatologie Privată')}
                  </span>
                </Card>
              </Link>
              <Link to={`/${lang}/prices`} data-id="element-1063">
                <Card className="p-4 hover:shadow-md transition-shadow h-full flex items-center" data-id="element-1064">
                  <span className="text-teal-600 font-medium" data-id="element-1065">
                    {pick('Our Prices', 'Наші ціни', 'Наши цены', 'Prețurile Noastre')}
                  </span>
                </Card>
              </Link>
            </div>
          </div>
        </div>

        {/* CTA */}
        <section className="py-16 bg-teal-900" data-id="element-1066">
          <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-1067">
            <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1068">
              {pick('Book an appointment without unnecessary formalities', 'Запишіться на прийом без зайвих формальностей', 'Запишитесь на прием без лишних формальностей', 'Programați o vizită fără formalități inutile')}
            </h2>
            <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto" data-id="element-1069">
              {pick('Simple registration, transparent prices, and quality treatment.', 'Проста реєстрація, прозорі ціни та якісне лікування.', 'Простая регистрация, прозрачные цены и качественное лечение.', 'Înregistrare simplă, prețuri transparente și tratament de calitate.')}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1070">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-1071">
                <Button variant="primaryInverse" size="lg" className="w-full gap-2" data-id="element-1072">
                  <Calendar className="w-5 h-5" data-id="element-1073" />
                  {pick('Book Online', 'Записатись онлайн', 'Записаться онлайн', 'Programare Online')}
                </Button>
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-1074">
                <Button variant="whatsappInverse" size="lg" className="w-full gap-2" data-id="element-1075">
                  <MessageCircle className="w-5 h-5" data-id="element-1076" />
                  WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </section>
      </article>
    </Layout>;
}